import React, { useEffect, useMemo, useRef, useState } from 'react';
import './TestimonialsSection.css';

const QUOTES = [
  {
    text: "You can't buy happiness, but you can buy ice cream — and that's kind of the same thing.",
    author: 'Unknown',
  },
  {
    text: 'Without ice cream, there would be darkness and chaos.',
    author: 'Don Kardong',
  },
  {
    text: 'Life is like ice cream — enjoy it before it melts.',
    author: 'Unknown',
  },
  {
    text: 'I scream. You scream. We all scream for ice cream!',
    author: 'Howard Johnson et al.',
  },
  {
    text: 'The only good thing about being wounded in the buttocks…is the ice cream.',
    author: 'Forrest Gump',
  },
];

export default function TestimonialsSection() {
  const sectionRef = useRef(null);
  const viewportRef = useRef(null);
  const slideRefs = useRef([]);

  const [index, setIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const reducedMotion = useMemo(() => {
    try { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
  }, []);

  const len = QUOTES.length;
  const go = (n) => setIndex((i) => (n + len) % len);
  const next = () => go(index + 1);
  const prev = () => go(index - 1);

  // IntersectionObserver: pause autoplay when not visible
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      setIsVisible(!!(e && e.isIntersecting));
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Autoplay loop
  useEffect(() => {
    if (reducedMotion || !isVisible || isHovered) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % len), 5000);
    return () => clearInterval(id);
  }, [reducedMotion, isVisible, isHovered, len]);

  // Fix viewport height to the tallest slide without using ResizeObserver
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    let raf = 0;
    let last = 0;
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        try {
          const heights = slideRefs.current.map(el => (el ? el.scrollHeight : 0));
          const maxH = Math.max(0, ...heights);
          if (maxH > 0 && maxH !== last) {
            vp.style.height = `${maxH}px`;
            last = maxH;
          }
        } catch {}
      });
    };
    schedule();
    const onResize = () => schedule();
    window.addEventListener('resize', onResize);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [len]);

  return (
    <section ref={sectionRef} className="section testimonials-section" id="testimonials">
      <div
        className="container t-carousel"
        role="region"
        aria-label="Testimonials"
      >
        <div
          className="t-viewport"
          ref={viewportRef}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onFocusCapture={() => setIsHovered(true)}
          onBlurCapture={() => setIsHovered(false)}
        >
          {QUOTES.map((q, i) => (
            <div
              key={i}
              ref={(el) => (slideRefs.current[i] = el)}
              id={`t-slide-${i}`}
              className={`t-slide ${i === index ? 'is-active' : ''}`}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${len}`}
              aria-hidden={i === index ? 'false' : 'true'}
            >
              <div className="t-card">
                <blockquote className="t-quote">“{q.text}”</blockquote>
                <div className="t-author">— {q.author}</div>
              </div>
            </div>
          ))}
        </div>

        {null}
      </div>
    </section>
  );
}
